import React, { useState, useContext } from "react";
import {
  Button,
  FormGroup,
  Label,
  Row,
  Col,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import * as Yup from "yup";
import moment from "moment";
import Select from "react-select";
import { Field, Formik } from "formik";
import { cloneDeep, find, isEmpty, merge } from "lodash";

import API from "utils/API";
import { AppContext } from "context";
import CoveredEntity from "components/CoveredEntity";
import Calendar from "assets/icon/calendar-icon.svg";
import NPIProviderList from "components/NPIProviderList";
import FormikDatePicker from "components/common/Fields/FormikDatePicker";
import FormikSelectField from "components/common/Fields/FormikSelectField";
import FormikInputField from "components/common/Fields/FormikInputField";

const validationSchema = Yup.object({
  coveredEntityID: Yup.string().required("Please select covered entity"),
});

const SearchContainer = (props) => {
  const [patientsLoading, setPatientsLoading] = useState(false);
  const { onSearch, onReset, claimStatusList } = props;
  let {
    states: { claimsManager },
    setStates,
    claimsInitialValues,
  } = useContext(AppContext);
  const {
    pharmacies,
    patients,
    coveredEntity,
    searchPatientTerm,
    searchTerms,
    patient,
    provider,
  } = claimsManager;
  const {
    coveredEntityID,
    pharmacyID,
    patientID,
    providerID,
    claimStatusID,
    startDate,
    endDate,
  } = searchTerms;
  const { tenantID } = coveredEntity;

  const updateStates = (claimsManager) => {
    setStates("claimsManager", claimsManager);
  };

  console.log("pharmacies", pharmacyID);

  const handleSubmit = async (val, actions) => {
    const { setSubmitting } = actions;
    const values = cloneDeep(val);
    values.providerNPI = providerID;
    if (pharmacyID) values.pharmacyID = pharmacyID;
    if (patientID) values.patientID = patientID;
    if (claimStatusID) values.claimStatusID = claimStatusID;
    if (startDate) values.startDate = startDate;
    if (endDate) values.endDate = endDate;
    claimsManager.search.currentPage = 1;
    updateStates(claimsManager);
    await onSearch(values);
    setSubmitting(false);
  };

  const findPatient = async (value) => {
    if (isEmpty(coveredEntity)) return;
    claimsManager.searchPatientTerm = value;
    updateStates(claimsManager);
    if (!value) return;
    setPatientsLoading(true);
    try {
      const { data } = await API.get(
        `/${tenantID}/AutoSearch/Patient/${value}`
      );
      claimsManager.patients = data;
      updateStates(claimsManager);
      setPatientsLoading(false);
    } catch (error) {
      setPatientsLoading(false);
    }
  };

  const onSelectPatient = (patient) => {
    if (patient) {
      claimsManager.patient = patient;
      claimsManager.searchTerms.patientID = patient.id;
    } else {
      claimsManager.patient = "";
      claimsManager.searchTerms.patientID = "";
    }
    updateStates(claimsManager);
  };

  const handleCoverdEntityChange = async (value, setFieldValue, entity) => {
    if (entity) {
      const { coveredEntityID } = entity;
      setFieldValue("coveredEntityID", value);
      claimsManager.pharmacies = [];
      claimsManager.searchTerms.pharmacyID = "";
      claimsManager.coveredEntity = entity;
      claimsManager.searchTerms.coveredEntityID = coveredEntityID;
      fetchPharmacyName(coveredEntityID);
    } else {
      setFieldValue("coveredEntityID", "");
      claimsManager.pharmacies = [];
      claimsManager.searchTerms.pharmacyID = "";
      claimsManager.coveredEntity = {};
      claimsManager.searchTerms.coveredEntityID = "";
    }
    updateStates(claimsManager);
  };

  const fetchPharmacyName = async (coveredEntityID) => {
    try {
      const { data } = await API.get(
        `RCMConfig/coveredentity/pharmacies/${coveredEntityID}`,
        {
          hasDefaultURL: true,
        }
      );
      claimsManager.pharmacies = data;
      console.log("data", data);
      updateStates(claimsManager);
    } catch (error) {
      console.log(error.message);
    }
  };

  const handlePharmacyChange = ({ target: { value } }) => {
    if (value) {
      const findObj = find(pharmacies, {
        pharmacyID: parseInt(value),
      });
      claimsManager.pharmacy = findObj;
      claimsManager.searchTerms.pharmacyID = findObj.pharmacyID;
    } else {
      claimsManager.pharmacy = {};
      claimsManager.searchTerms.pharmacyID = "";
    }
    updateStates(claimsManager);
  };

  const handleDateClick = (id) => {
    const calendarButton = document.getElementById(id);
    if (calendarButton) calendarButton.click();
  };

  const onSelect = (provider) => {
    if (provider) {
      claimsManager.provider = provider;
      claimsManager.searchTerms.providerID = provider.id;
    } else {
      claimsManager.provider = "";
      claimsManager.searchTerms.patientID = "";
    }
    updateStates(claimsManager);
  };

  const handleOnChange = ({ target: { value } }) => {
    claimsManager.searchTerms.rxNumber = value;
    updateStates(claimsManager);
  };

  const handleClaimStatus = ({ target: { value } }) => {
    if (value) {
      const statusID = parseInt(value);
      const findObj = find(claimStatusList, {
        id: statusID,
      });
      claimsManager.claimStatus = findObj;
      claimsManager.searchTerms.claimStatusID = statusID;
    } else {
      claimsManager.claimStatus = {};
      claimsManager.searchTerms.claimStatusID = "";
    }
    updateStates(claimsManager);
  };

  const handleStartDate = (startDate) => {
    claimsManager.searchTerms.startDate = moment(new Date(startDate)).format(
      "MM/DD/YYYY"
    );
    updateStates(claimsManager);
  };

  const handleEndDate = (endDate) => {
    claimsManager.searchTerms.endDate = moment(new Date(endDate)).format(
      "MM/DD/YYYY"
    );
    updateStates(claimsManager);
  };

  const handleReset = ({ resetForm }) => {
    resetForm();
    onReset();
    updateStates(merge(claimsManager, claimsInitialValues));
  };

  return (
    <Formik
      initialValues={searchTerms}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
      children={(props) => {
        const { isSubmitting, handleSubmit, resetForm, setFieldValue } = props;
        return (
          <div>
            <h5 className="f-15 text-primary font-weight-700">
              Search Claims Manager
            </h5>
            <Row sm={5}>
              <Col md={2} sm={3} xs={6}>
                {" "}
                <FormGroup>
                  <Label for="coveredEntityID">Covered Entity</Label>
                  <CoveredEntity
                    value={coveredEntityID}
                    name="coveredEntityID"
                    onChange={(value, enty) =>
                      handleCoverdEntityChange(value, setFieldValue, enty)
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={2} sm={3} xs={6}>
                {" "}
                <FormGroup>
                  <Label for="pharmacyID">Pharmacy Name</Label>
                  <Field
                    name="pharmacyID"
                    type="text"
                    value={pharmacyID}
                    onChange={(e) => handlePharmacyChange(e, setFieldValue)}
                    component={FormikSelectField}
                    options={pharmacies}
                    defaultoption="--Select--"
                    keys={{
                      id: "pharmacyID",
                      label: "pharmacyName",
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={2} sm={3} xs={6}>
                <FormGroup>
                  <Label for="patientID">Patient</Label>
                  <Select
                    className="form-field-large inner-shadow-field" //npi-name
                    classNamePrefix="select"
                    IndicatorSeparator
                    isClearable={true}
                    isSearchable={true}
                    isLoading={patientsLoading}
                    placeholder=""
                    name="color"
                    options={patients}
                    value={patient}
                    inputValue={searchPatientTerm}
                    onInputChange={findPatient}
                    onChange={onSelectPatient}
                    getOptionLabel={(option) => option.description}
                    getOptionValue={(option) => option.id}
                    // isDisabled={!tenantID}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                    }}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 99,
                      }),
                    }}
                  />
                </FormGroup>
              </Col>

              <Col md={2} sm={3} xs={6}>
                {" "}
                <FormGroup>
                  <Label for="rxNumber">Rx#</Label>
                  <Field
                    id="rxNumber"
                    name="rxNumber"
                    type="text"
                    autoComplete="off"
                    component={FormikInputField}
                    onKeyUp={handleOnChange}
                  />
                </FormGroup>
              </Col>

              <Col md={2} sm={3} xs={6}>
                <FormGroup>
                  <Label>NPI/Provider Name</Label>
                  <NPIProviderList
                    tenantID={tenantID}
                    onSelect={onSelect}
                    selectedProvider={provider}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={2} sm={3} xs={6}>
                <FormGroup>
                  <Label>Status</Label>
                  <Field
                    name="claimStatus"
                    type="text"
                    value={claimStatusID}
                    onChange={handleClaimStatus}
                    component={FormikSelectField}
                    options={claimStatusList}
                    defaultoption="--Select--"
                    keys={{
                      id: "id",
                      label: "description",
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={3} sm={6}>
                <Row>
                  <Col>
                    <FormGroup>
                      <Label>Start Date</Label>
                      <InputGroup>
                        <Field
                          type="text"
                          id="startDate"
                          name="startDate"
                          autoComplete="off"
                          placeholder="Select a date"
                          value={startDate}
                          component={FormikDatePicker}
                          onChange={handleStartDate}
                        />
                        <InputGroupText
                          onClick={() => handleDateClick("startDate")}
                        >
                          <img src={Calendar} alt="" />
                        </InputGroupText>
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col xs="auto" className="pt-3 mt-3">
                    <Label className="mt-1">-</Label>
                  </Col>
                  <Col>
                    <FormGroup>
                      <Label>End Date</Label>
                      <InputGroup>
                        <Field
                          type="text"
                          id="endDate"
                          name="endDate"
                          autoComplete="off"
                          placeholder="Select a date"
                          value={endDate}
                          component={FormikDatePicker}
                          onChange={handleEndDate}
                        />
                        <InputGroupText
                          onClick={() => handleDateClick("endDate")}
                        >
                          <img src={Calendar} alt="" />
                        </InputGroupText>
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>
              </Col>

              <Col md="auto" className="pt-4 mt-1">
                <Label>{""}</Label>
                <Button
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                  color="primary"
                  className="ms-3"
                >
                  Search
                </Button>
                <Button color="primary" onClick={() => handleReset(props)}>
                  Reset
                </Button>
              </Col>
            </Row>
          </div>
        );
      }}
    ></Formik>
  );
};

export default SearchContainer;
